import { useCallback, useEffect, useState } from 'react';

export type OnboardingStepKey = 'create_project' | 'invite_member' | 'enable_ai';

interface OnboardingState {
  completed: boolean;
  steps: Record<OnboardingStepKey, boolean>;
  dismissed: boolean;
}

const STORAGE_KEY = 'pm_onboarding_state_v1';
const LEGACY_KEY = 'onboarding_completed';

const DEFAULT_STATE: OnboardingState = {
  completed: false,
  steps: { create_project: false, invite_member: false, enable_ai: false },
  dismissed: false,
};

function readState(): OnboardingState {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<OnboardingState>;
      return { ...DEFAULT_STATE, ...parsed, steps: { ...DEFAULT_STATE.steps, ...(parsed.steps ?? {}) } };
    }
    // Migración: si el flag legacy existe, dar el onboarding por completado.
    if (window.localStorage.getItem(LEGACY_KEY) === 'true') {
      return { ...DEFAULT_STATE, completed: true, dismissed: true };
    }
  } catch {
    // ignore
  }
  return DEFAULT_STATE;
}

function writeState(state: OnboardingState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    if (state.completed) window.localStorage.setItem(LEGACY_KEY, 'true');
  } catch {
    // ignore
  }
}

export function useOnboarding() {
  const [state, setState] = useState<OnboardingState>(() => readState());

  useEffect(() => { writeState(state); }, [state]);

  const completedCount = Object.values(state.steps).filter(Boolean).length;
  const totalSteps = Object.keys(state.steps).length;
  const progress = Math.round((completedCount / totalSteps) * 100);

  const markStep = useCallback((step: OnboardingStepKey) => {
    setState(prev => {
      const nextSteps = { ...prev.steps, [step]: true };
      const allDone = Object.values(nextSteps).every(Boolean);
      return { ...prev, steps: nextSteps, completed: allDone || prev.completed };
    });
  }, []);

  const completeAll = useCallback(() => {
    setState(prev => ({
      ...prev,
      completed: true,
      steps: { create_project: true, invite_member: true, enable_ai: true },
    }));
  }, []);

  const dismiss = useCallback(() => {
    setState(prev => ({ ...prev, dismissed: true }));
  }, []);

  const reset = useCallback(() => {
    setState(DEFAULT_STATE);
    try { window.localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
  }, []);

  return {
    ...state,
    completedCount,
    totalSteps,
    progress,
    markStep,
    completeAll,
    dismiss,
    reset,
    /** Debe mostrarse el wizard automáticamente (primer login). */
    shouldShowWizard: !state.completed && !state.dismissed && completedCount === 0,
    /** Debe mostrarse el checklist en sidebar. */
    shouldShowChecklist: !state.completed && !state.dismissed,
  };
}
